import React, { useCallback, useContext, useEffect, useRef } from 'react';

import { config } from 'component/Search/Datepicker/__config';
import { FormType } from 'component/Search/Datepicker/types';
import SingleLine from 'component/Search/Form/SingleLine';
import StickyFilter from 'component/Search/StickyFilter';

import { useSearch } from 'pages/hotels/component/Search/__hooks/useSearch';
import List from 'pages/hotels/component/Search/__partials/Filter/List';
import DebounceProviderWrapper from 'pages/hotels/component/Search/__partials/Filter/List/DebounceProviderWrapper';
import ScrollView from 'pages/hotels/component/Search/__partials/Filter/List/ScrollView';

import { defaultSearch } from 'config/SearchForm/defaults';
import { parse } from 'date-fns';
import { Provider as FilterProvider } from 'jotai';

import { filterStore } from 'data/Atom/Store';
import { travelDateList } from 'data/mocks/Search/TravelPeriod/travelDateList';

import Portal from 'utility/Bind/Portal/Portal';
import { validateAndReplaceUrl } from 'utility/Convert/Search/replaceUrl';
import { convertUrlToSearch } from 'utility/Convert/Search/urlToSearch';
import { CommunicationContext } from 'utility/Provide/Context/PageContext';

import { BackendInformationKeys } from 'types/legacy/backendInformation';

import styles from './Search.module.scss';

interface SearchProps {
    target: string;
}

const Search = ({ target }: SearchProps) => {
    const { initSearch, removeParamInSearch } = useSearch();
    const isInitiatedSearch = useRef(false);
    const namespace = useContext(CommunicationContext);
    const singleLineTarget = '#portal-s3-single-line-searchform';

    const updateSearchUrl = () => {
        removeParamInSearch(['forceOpenMap']);
    };
    const { react, components } = namespace;

    react.bind('mapLayerClose', updateSearchUrl);

    const getSearchDefaults = useCallback(() => {
        const urlParams = convertUrlToSearch({
            searchUrl: validateAndReplaceUrl(location.href),
        });
        const departureDate =
            urlParams.travelPeriod.departureDate ||
            parse(
                components.backendInformationFunc.getData(
                    BackendInformationKeys.TravelDates
                ).package.departureDate.value,
                config.format,
                new Date()
            );
        const returnDate =
            urlParams.travelPeriod.returnDate ||
            parse(
                components.backendInformationFunc.getData(
                    BackendInformationKeys.TravelDates
                ).package.returnDate.value,
                config.format,
                new Date()
            );
        const occupancy = urlParams.occupancy || defaultSearch.occupancy;
        const duration =
            urlParams.travelPeriod.duration ||
            defaultSearch.travelPeriod.duration;

        return { departureDate, returnDate, occupancy, duration };
    }, [components.backendInformationFunc]);

    useEffect(() => {
        // make sure not to init the search more than 1 time
        if (!isInitiatedSearch.current) {
            initSearch(validateAndReplaceUrl(location.href));
            isInitiatedSearch.current = true;
        }
    }, [initSearch]);

    return (
        <>
            <Portal root={singleLineTarget}>
                <div className={styles.SingleLineWrapper}>
                    <SingleLine
                        formType={FormType.Package}
                        travelDates={travelDateList}
                        initialValue={getSearchDefaults()}
                    />
                </div>
            </Portal>
            <Portal root={target}>
                <div className={styles.Wrapper}>
                    <FilterProvider store={filterStore}>
                        <DebounceProviderWrapper>
                            <StickyFilter scrollView={<ScrollView />}>
                                <List />
                            </StickyFilter>
                        </DebounceProviderWrapper>
                    </FilterProvider>
                </div>
            </Portal>
        </>
    );
};

export default Search;
